import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Flex, Box } from 'reflexbox'
import { Redirect } from 'react-router'
import * as R from 'ramda'
import P from 'helpers/predicates'
import dialogHoc from 'containers/Async/AsyncDialog'
import T from 'i18n-react'
import queryString from 'query-string'
import {
  lifecycle,
  compose,
  withHandlers,
  withState,
  branch,
  renderComponent,
  withProps
} from 'recompose'
import _ from 'lodash'
import { toJSON } from 'helpers/Utils'

import injectSagas from 'helpers/injectSagas'
import { onEnter } from 'helpers/events'

import Autocomplete from './Autocomplete'
import Card from './Card'
import CardTitle from './CardTitle'
import Layout from './Layout'
import Panel from './Panel'

import ReportSummaryCard from './ReportSummaryCard'


import {
  MODULE_NAME,
  FETCH_PRODUCT_SKUS,
  FETCH_GREENLIGHT_REPORT
} from './constants'

import {
  onInputChange,
  onEnter as onEnterValue,
  onSkuChange,
  updateTableFilter,
  updateTableSort,
  clearTableFilter,
  clearTableSort,
  clearSearch,
  mount
} from './actions'

import {
  skuSelector,
  searchSelector,
    tableFilterSelector,
    tableSortSelector,
  skuSourceSelector,
  reportSourceSelector
} from './selectors'

import sagas from './sagas'
import IconButton from "./IconButton";

const mapStateToProps = (state) => {
  return {
    searchFilter: searchSelector(state),
    skuSource: skuSourceSelector(state),
    sku: skuSelector(state),
    tableFilter: tableFilterSelector(state),
    tableSort: tableSortSelector(state),
    reportSource: reportSourceSelector(state)
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onInputChange: (value) => dispatch(onInputChange(value)),
    onEnter: onEnter((value) => dispatch(onEnterValue(value))),
    onSkuChange: (value) => dispatch(onSkuChange(value)),
    updateTableFilter: (value) => dispatch(updateTableFilter(value)),
    updateTableSort: (value) => dispatch(updateTableSort(value)),
    clearTableFilter: () => dispatch(clearTableFilter()),
    clearTableSort: () => dispatch(clearTableSort()),
    clearSearch: () => dispatch(clearSearch()),
    mount: (o) => dispatch(mount(o))
  }
}

const AsyncDialog = dialogHoc([ FETCH_PRODUCT_SKUS ])

class GreenlightIndex extends Component {
  onRowClick = (row) => {
    const { reportSource, onProjectIdChange } = this.props
    onProjectIdChange(reportSource[row].project)
  }

  onSkuChange = (sku) => {
    const { history } = this.props
    let query = queryString.stringify({ sku })
    let location = `${history.location.pathname}?${query}`
    history.replace(location)
    this.props.onSkuChange(sku)
  }

  onClearFilters = () => {
    const { history, sku } = this.props
    if(sku) {
      this.updateURL(sku, {}, {}, history)
      this.props.clearTableFilter()
      this.props.clearTableSort()
      this.props.clearSearch()
    }
  }

  updateURL(sku, tableFilter, tableSort, history) {
    let query = queryString.stringify({ sku, tableFilter: JSON.stringify(tableFilter), tableSort: JSON.stringify(tableSort)})
    let location = `${history.location.pathname}?${query}`
    history.replace(location)
  }

  onMenuSubmit = (filterObj) => {
    let {sku, tableFilter, tableSort, history} = this.props

    const columnKey = _.keys(filterObj)[0]
    const {filter, sort} = filterObj[columnKey]

    if(_.isEmpty(filter) || _.includes(tableFilter[columnKey], filter)) {
        //Do nothing
    } else if(_.isArray(tableFilter[columnKey])) {
        tableFilter[columnKey].push(filter)
    } else {
        tableFilter[columnKey] = [filter]
    }

    tableSort = sort ? {[columnKey]: sort} : tableSort

    this.updateURL(sku, tableFilter, tableSort, history)
    this.props.updateTableFilter(tableFilter)
    this.props.updateTableSort(tableSort)
  }

  removeFilter = (filter, colKey) => {
    let { tableFilter, tableSort, sku, history } = this.props

    if(_.isArray(tableFilter[colKey]) && (tableFilter[colKey].length > 1)) {
      tableFilter[colKey] = _.remove(tableFilter[colKey], (f) => f !== filter)
    } else {
      tableFilter[colKey] = undefined
    }

    this.updateURL(sku, tableFilter, tableSort, history)
    this.props.updateTableFilter(tableFilter)

  }

  render() {
    const {
      searchFilter,
      sku,
      tableFilter,
      tableSort,
      skuSource,
      reportSource,
      onInputChange,
      onEnter,
      ...rest
    } = this.props

    return (
      <Layout>
        <Panel>
          <Card>
            <Flex styleName='header'>
              <CardTitle>
                { T.translate('containers.report.greenlight.index.header') }
              </CardTitle>
              <Flex
                auto
                justify='flex-end'
              >
              </Flex>
              <Autocomplete
                label={T.translate('containers.report.greenlight.index.autocomplete')}
                onChange={this.onSkuChange}
                source={skuSource}
                value={sku}
                suggestionMatch='anywhere'
              />
            </Flex>
          </Card>
          <ReportSummaryCard
            reportSource={reportSource}
            tableFilter={tableFilter}
            tableSort={tableSort}
            searchFilter={searchFilter}
            sku={sku}
            onInputChange={onInputChange}
            removeFilter={this.removeFilter}
            onClearFilters={this.onClearFilters}
            onRowClick={this.onRowClick}
            onMenuSubmit={this.onMenuSubmit}
          />
        </Panel>
        <AsyncDialog title={T.translate('containers.report.greenlight.index.loading')}/>
      </Layout>
    )
  }
}

const withLifecycle = lifecycle({
  componentDidMount() {
    const { sku, filter, tableFilter, tableSort } = this.props.search
    this.props.mount({sku, tableFilter, tableSort})
  }
})

const withProject = compose(
  withProps(props => {
    const { sku, filter, tableFilter, tableSort } = queryString.parse(props.location.search)
    return { search: { sku , filter, tableFilter: toJSON(tableFilter), tableSort: toJSON(tableSort) } }
  }),
  withState('projectId', 'updateProjectId', null),
  withHandlers({
    onProjectIdChange: props => value => props.updateProjectId(value)
  }),
  withLifecycle,
  branch(
    ({ projectId }) => P.isNotNil(projectId),
    renderComponent(({ projectId }) => <Redirect push to={`/project/${projectId}/view`}/>)
  )
)

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withSagas = injectSagas({ [MODULE_NAME]: sagas })
const enhance = compose(
  withSagas,
  withConnect,
  withProject,
)
export default enhance(GreenlightIndex)
